import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControlLabel,
    Grid,
    Link,
    Paper,
    Skeleton,
    Switch,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { apiService } from '../services/api';

type DateParams = {
    week?: string;
    year?: string;
    startDate?: string;
    endDate?: string;
};

interface ProgramAnalysisFifaTabProps {
    enabled: boolean;
    dateParams: DateParams;
    podcastId?: string;
}

function formatDate(value?: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatNumber(value?: number | null): string {
    if (value == null) return '—';
    return value.toLocaleString('es-CL');
}

type ContentSortField = 'title' | 'programName' | 'podcastTitle' | 'publishedAt' | 'views';
type StreamSortField = 'title' | 'channelName' | 'programName' | 'actualStartTime' | 'avgViewers' | 'peakViewers';
type SortOrder = 'asc' | 'desc';
type SubTab = 'content' | 'streams';

function toggleSort<T extends string>(
    currentField: T,
    currentOrder: SortOrder,
    nextField: T,
    defaultOrder: SortOrder = 'desc',
): { field: T; order: SortOrder } {
    if (currentField === nextField) {
        return { field: nextField, order: currentOrder === 'asc' ? 'desc' : 'asc' };
    }
    return { field: nextField, order: defaultOrder };
}

const ProgramAnalysisFifaTab: React.FC<ProgramAnalysisFifaTabProps> = ({
    enabled,
    dateParams,
    podcastId,
}) => {
    const [subTab, setSubTab] = useState<SubTab>('content');
    const [page, setPage] = useState(1);
    const [includeRejected, setIncludeRejected] = useState(false);
    const [contentSortBy, setContentSortBy] = useState<ContentSortField>('views');
    const [contentSortOrder, setContentSortOrder] = useState<SortOrder>('desc');
    const [streamSortBy, setStreamSortBy] = useState<StreamSortField>('avgViewers');
    const [streamSortOrder, setStreamSortOrder] = useState<SortOrder>('desc');
    const pageSize = 50;

    const handleContentSort = (field: ContentSortField) => {
        const defaultOrder: SortOrder =
            field === 'title' || field === 'programName' || field === 'podcastTitle' ? 'asc' : 'desc';
        const next = toggleSort(contentSortBy, contentSortOrder, field, defaultOrder);
        setContentSortBy(next.field);
        setContentSortOrder(next.order);
        setPage(1);
    };

    const handleStreamSort = (field: StreamSortField) => {
        const defaultOrder: SortOrder =
            field === 'title' || field === 'channelName' || field === 'programName' ? 'asc' : 'desc';
        const next = toggleSort(streamSortBy, streamSortOrder, field, defaultOrder);
        setStreamSortBy(next.field);
        setStreamSortOrder(next.order);
        setPage(1);
    };

    const { data, isLoading, error } = useQuery({
        queryKey: [
            'fifa-classification',
            dateParams,
            podcastId,
            subTab,
            page,
            includeRejected,
            contentSortBy,
            contentSortOrder,
            streamSortBy,
            streamSortOrder,
        ],
        queryFn: () =>
            apiService.getFifaClassification({
                ...dateParams,
                podcastId,
                source: 'both',
                page,
                pageSize,
                includeRejected,
                contentSortBy,
                contentSortOrder,
                streamSortBy,
                streamSortOrder,
            }),
        enabled,
    });

    const contentSection = data?.content;
    const streamSection = data?.streams;
    const activeSection = subTab === 'content' ? contentSection : streamSection;
    const total = activeSection?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Tabs
                    value={subTab}
                    onChange={(_e, value: SubTab) => {
                        setSubTab(value);
                        setPage(1);
                    }}
                >
                    <Tab label="Content" value="content" />
                    <Tab label="Livestreams" value="streams" />
                </Tabs>
                <FormControlLabel
                    control={
                        <Switch
                            checked={includeRejected}
                            onChange={(e) => {
                                setIncludeRejected(e.target.checked);
                                setPage(1);
                            }}
                            size="small"
                        />
                    }
                    label="Include regex-only rejects"
                />
                <Box sx={{ flex: 1 }} />
                <Button
                    size="small"
                    variant="outlined"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                    Prev
                </Button>
                <Typography variant="body2">
                    Page {page} of {totalPages} ({total} total)
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                    Next
                </Button>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary">
                                {contentSection?.total ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                FIFA content items
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary">
                                {streamSection?.total ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                FIFA livestreams
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {error ? (
                <Alert severity="error">Failed to load FIFA classification data</Alert>
            ) : isLoading ? (
                <Skeleton variant="rectangular" height={420} />
            ) : subTab === 'content' ? (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 48 }}>#</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={contentSortBy === 'title'}
                                        direction={contentSortBy === 'title' ? contentSortOrder : 'asc'}
                                        onClick={() => handleContentSort('title')}
                                    >
                                        Title
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={contentSortBy === 'programName'}
                                        direction={contentSortBy === 'programName' ? contentSortOrder : 'asc'}
                                        onClick={() => handleContentSort('programName')}
                                    >
                                        Program
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={contentSortBy === 'podcastTitle'}
                                        direction={contentSortBy === 'podcastTitle' ? contentSortOrder : 'asc'}
                                        onClick={() => handleContentSort('podcastTitle')}
                                    >
                                        Podcast
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={contentSortBy === 'publishedAt'}
                                        direction={contentSortBy === 'publishedAt' ? contentSortOrder : 'desc'}
                                        onClick={() => handleContentSort('publishedAt')}
                                    >
                                        Published
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={contentSortBy === 'views'}
                                        direction={contentSortBy === 'views' ? contentSortOrder : 'desc'}
                                        onClick={() => handleContentSort('views')}
                                    >
                                        Views (7d)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>LLM reason</TableCell>
                                <TableCell align="center">Link</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(contentSection?.items ?? []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            No FIFA-classified content in this period. Run{' '}
                                            <code>yarn sync:fifa-world-cup</code> in pipelines first.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                contentSection!.items.map((item) => (
                                    <TableRow key={item.contentId} hover>
                                        <TableCell align="center">{item.ranking}</TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="body2" noWrap title={item.title}>
                                                {item.title}
                                            </Typography>
                                            {!item.isFifaWorldCup && (
                                                <Chip label="rejected" size="small" color="warning" sx={{ mt: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>{item.programName ?? '—'}</TableCell>
                                        <TableCell>{item.podcastTitle ?? '—'}</TableCell>
                                        <TableCell>{formatDate(item.publishedAt)}</TableCell>
                                        <TableCell align="right">{formatNumber(item.views)}</TableCell>
                                        <TableCell sx={{ maxWidth: 240 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.llmReason}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.url ? (
                                                <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                                    <OpenInNewIcon fontSize="small" />
                                                </Link>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 48 }}>#</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={streamSortBy === 'title'}
                                        direction={streamSortBy === 'title' ? streamSortOrder : 'asc'}
                                        onClick={() => handleStreamSort('title')}
                                    >
                                        Title
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={streamSortBy === 'channelName'}
                                        direction={streamSortBy === 'channelName' ? streamSortOrder : 'asc'}
                                        onClick={() => handleStreamSort('channelName')}
                                    >
                                        Channel
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={streamSortBy === 'programName'}
                                        direction={streamSortBy === 'programName' ? streamSortOrder : 'asc'}
                                        onClick={() => handleStreamSort('programName')}
                                    >
                                        Program
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={streamSortBy === 'actualStartTime'}
                                        direction={streamSortBy === 'actualStartTime' ? streamSortOrder : 'desc'}
                                        onClick={() => handleStreamSort('actualStartTime')}
                                    >
                                        Started
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={streamSortBy === 'avgViewers'}
                                        direction={streamSortBy === 'avgViewers' ? streamSortOrder : 'desc'}
                                        onClick={() => handleStreamSort('avgViewers')}
                                    >
                                        Avg viewers
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={streamSortBy === 'peakViewers'}
                                        direction={streamSortBy === 'peakViewers' ? streamSortOrder : 'desc'}
                                        onClick={() => handleStreamSort('peakViewers')}
                                    >
                                        Peak viewers
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>LLM reason</TableCell>
                                <TableCell align="center">Link</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(streamSection?.items ?? []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            No FIFA-classified livestreams in this period. Run{' '}
                                            <code>yarn sync:fifa-world-cup</code> in pipelines first.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                streamSection!.items.map((item) => (
                                    <TableRow key={item.streamId} hover>
                                        <TableCell align="center">{item.ranking}</TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="body2" noWrap title={item.title}>
                                                {item.title}
                                            </Typography>
                                            {!item.isFifaWorldCup && (
                                                <Chip label="rejected" size="small" color="warning" sx={{ mt: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>{item.channelName ?? item.channelId ?? '—'}</TableCell>
                                        <TableCell>{item.programName ?? '—'}</TableCell>
                                        <TableCell>{formatDate(item.actualStartTime)}</TableCell>
                                        <TableCell align="right">{formatNumber(item.avgViewers)}</TableCell>
                                        <TableCell align="right">{formatNumber(item.peakViewers)}</TableCell>
                                        <TableCell sx={{ maxWidth: 240 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.llmReason}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.url ? (
                                                <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                                    <OpenInNewIcon fontSize="small" />
                                                </Link>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
};

export default ProgramAnalysisFifaTab;
